/**
 * Reusable Shift Status Badge Component
 * Displays shift status with consistent styling and behavior
 */

import React from 'react';
import { ShiftStatus, SHIFT_STATUS_CONFIG } from '@/lib/scheduling/shift-status-types';
import { 
  getShiftStatusStyles, 
  getShiftStatusIcon, 
  StyleVariant,
  STATUS_TRANSITION_CLASSES 
} from '@/lib/scheduling/shift-status-styles';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Clock, 
  PlayCircle, 
  Play, 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  UserX,
  ArrowRightFromLine 
} from 'lucide-react';

// Icon component mapping
const ICON_COMPONENTS = {
  Clock,
  PlayCircle,
  Play,
  CheckCircle,
  AlertCircle,
  XCircle,
  UserX,
  ArrowRightFromLine
} as const;

export interface ShiftStatusBadgeProps {
  status: ShiftStatus;
  variant?: StyleVariant;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showText?: boolean;
  showTooltip?: boolean;
  animated?: boolean;
  onClick?: () => void;
  className?: string;
  'data-testid'?: string;
  // Timing precision for enhanced display
  lateMinutes?: number;
  minutesEarly?: number;
  overtimeMinutes?: number;
}

export const ShiftStatusBadge: React.FC<ShiftStatusBadgeProps> = ({
  status,
  variant = StyleVariant.COMPACT,
  size = 'md',
  showIcon = true,
  showText = true,
  showTooltip = true,
  animated = false,
  onClick,
  className = '',
  'data-testid': testId,
  lateMinutes,
  minutesEarly,
  overtimeMinutes,
}) => {
  const config = SHIFT_STATUS_CONFIG[status];
  const styles = getShiftStatusStyles(status, variant, size, showText);
  const iconName = getShiftStatusIcon(status, 'lucide') as keyof typeof ICON_COMPONENTS;
  const IconComponent = ICON_COMPONENTS[iconName] || Clock;

  // Enhanced label with timing precision
  const getEnhancedLabel = (): string => {
    // Handle composite statuses first
    if (status === ShiftStatus.TOO_EARLY_OVERTIME) {
      const earlyPart = minutesEarly ? `Early (${minutesEarly}m)` : 'Early';
      const otPart = overtimeMinutes ? `OT (${overtimeMinutes}m)` : 'Overtime';
      return `${earlyPart} + ${otPart}`;
    }
    if (status === ShiftStatus.LATE_OVERTIME) {
      const latePart = lateMinutes ? `Late (${lateMinutes}m)` : 'Late';
      const otPart = overtimeMinutes ? `OT (${overtimeMinutes}m)` : 'Overtime';
      return `${latePart} + ${otPart}`;
    }
    if (status === ShiftStatus.TOO_EARLY_LEFT_EARLY) {
      const earlyPart = minutesEarly ? `Early (${minutesEarly}m)` : 'Early';
      return `${earlyPart} + Left Early`;
    }
    if (status === ShiftStatus.LATE_LEFT_EARLY) {
      const latePart = lateMinutes ? `Late (${lateMinutes}m)` : 'Late';
      return `${latePart} + Left Early`;
    }
    // Handle single statuses
    if (status === ShiftStatus.LATE && lateMinutes) {
      return `${config.label} (${lateMinutes}m)`;
    }
    if (status === ShiftStatus.TOO_EARLY && minutesEarly) {
      return `${config.label} (${minutesEarly}m)`;
    }
    if (status === ShiftStatus.OVERTIME && overtimeMinutes) {
      return `${config.label} (${overtimeMinutes}m)`;
    }
    return config.label;
  };

  const badgeClasses = [
    styles.container,
    onClick ? 'cursor-pointer hover:opacity-80' : '',
    animated ? STATUS_TRANSITION_CLASSES.enter : '',
    className
  ].filter(Boolean).join(' ');

  const BadgeContent = (
    <div 
      className={badgeClasses}
      onClick={onClick}
      data-testid={testId}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {showIcon && (
        <IconComponent
          className={styles.icon}
          aria-hidden="true"
        />
      )}
      {showText && (
        <span className="font-medium">
          {getEnhancedLabel()}
        </span>
      )}
    </div>
  );

  if (!showTooltip) {
    return BadgeContent;
  }

  // Note: TooltipProvider is hoisted to ComposedShiftProvider
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {BadgeContent}
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-sm">
          <div className="font-medium">{config.label}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {config.description}
          </div>
          {config.requiresAttention && (
            <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
              ⚠️ Requires attention
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

// Specialized variants for common use cases
export const CompactShiftStatusBadge: React.FC<Omit<ShiftStatusBadgeProps, 'variant' | 'size'>> = (props) => (
  <ShiftStatusBadge {...props} variant={StyleVariant.COMPACT} size="sm" />
);

export const PillShiftStatusBadge: React.FC<Omit<ShiftStatusBadgeProps, 'variant'>> = (props) => (
  <ShiftStatusBadge {...props} variant={StyleVariant.PILL} />
);

export const OutlinedShiftStatusBadge: React.FC<Omit<ShiftStatusBadgeProps, 'variant'>> = (props) => (
  <ShiftStatusBadge {...props} variant={StyleVariant.OUTLINED} />
);

export const MinimalShiftStatusBadge: React.FC<Omit<ShiftStatusBadgeProps, 'variant' | 'showTooltip'>> = (props) => (
  <ShiftStatusBadge {...props} variant={StyleVariant.MINIMAL} showTooltip={false} />
);

// Calendar-specific badge variant
export const CalendarShiftStatusBadge: React.FC<Omit<ShiftStatusBadgeProps, 'variant' | 'showIcon' | 'showTooltip'>> = (props) => (
  <ShiftStatusBadge 
    {...props} 
    variant={StyleVariant.CALENDAR} 
    showIcon={false} 
    showTooltip={true}
  />
);